import React from "react";
import { applyForJob } from "../../services/forms";
import { getBase64 } from "../../utils/base64";
import "./apply.css";

const SKILLS = [
  "AI / ML",
  "Backend",
  "Frontend",
  "Mobile",
  "Cloud / DevOps",
  "Data",
  "Design",
  "QA",
];

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ApplyForJob = () => {
  const fileRef = React.useRef();
  const [skills, setSkills] = React.useState([]);
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phoneNumber: "",
    rate: "",
    links: "",
  });
  const [file, setFile] = React.useState({ name: "", body: "" });
  const [errors, setErrors] = React.useState({});
  const [sending, setSending] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [note, setNote] = React.useState({ text: "", bad: false });

  const toggleSkill = (s) =>
    setSkills((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const change = (e) => {
    const { name, value } = e.target;
    if (name === "rate" && value !== "" && isNaN(value)) return;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const pickFile = () => fileRef.current && fileRef.current.click();

  const onFile = () => {
    const f = fileRef.current.files[0];
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      setNote({ text: "That file is over 25MB. Please attach a smaller one.", bad: true });
      fileRef.current.value = "";
      return;
    }
    setNote({ text: "", bad: false });
    getBase64(f)
      .then((body) => setFile({ name: f.name, body }))
      .catch(() => setNote({ text: "Couldn't read that file. Try another.", bad: true }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!form.email.trim()) next.email = "Please enter your email";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "That email doesn't look right";
    if (!form.rate.trim()) next.rate = "Please enter your hourly rate";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setNote({ text: "", bad: false });
    if (!validate()) return;

    // The API appends Name/Email/Rate to `body`, so anything extra we want in
    // the email (skills, phone) has to be put in `body` ourselves.
    const extras = [];
    if (skills.length) extras.push("Skills: " + skills.join(", "));
    if (form.links.trim()) extras.push("Links: " + form.links.trim());
    if (form.phoneNumber.trim()) extras.push("Phone: " + form.phoneNumber.trim());

    const payload = {
      body: extras.length ? extras.join("<br>") + "<br>" : "",
      name: form.name.trim(),
      email: form.email.trim(),
      phoneNumber: form.phoneNumber.trim(),
      rate: form.rate.trim(),
      attachment: { body: file.body, name: file.name },
    };

    setSending(true);
    try {
      const res = await applyForJob(payload);
      if (res && res.status === 200) setDone(true);
      else setNote({ text: "Something went wrong. Please email contact@cowork.dev.", bad: true });
    } catch (err) {
      setNote({ text: "Couldn't send just now. Please email contact@cowork.dev.", bad: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="cwa">
      <header className="cwa__nav">
        <div className="cwa__navInner">
          <a className="cwa__mark" href="/" aria-label="Cowork Development home">
            c<span className="cwa__dot" aria-hidden="true"></span>work
            <span className="cwa__suffix">Development</span>
          </a>
          <a className="cwa__back" href="/">← Back to site</a>
        </div>
      </header>

      <div className="cwa__wrap">
        {done ? (
          <div className="cwa__card">
            <div className="cwa__done">
              <div className="cwa__doneMark" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              <h1 className="cwa__doneTitle">Application received</h1>
              <p className="cwa__doneBody">
                Thanks for applying. An engineer will review what you've built and
                get back to you — we read every application ourselves.
              </p>
            </div>
          </div>
        ) : (
          <>
            <p className="cwa__eyebrow">For engineers</p>
            <h1 className="cwa__title">Join the network.</h1>
            <p className="cwa__lead">
              We're a global community of experienced engineers building AI-powered
              products — not a freelancer marketplace. Tell us what you've built and
              we'll take it from there.
            </p>
            <ul className="cwa__points">
              <li>Reviewed by an engineer, not a recruiter</li>
              <li>Remote-first, wherever you are</li>
              <li>Work on production AI and product problems</li>
            </ul>

            <form className="cwa__card" onSubmit={submit} noValidate>
              <p className="cwa__legend">
                What do you work on? <span className="cwa__hint">(select any)</span>
              </p>
              <div className="cwa__chips">
                {SKILLS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`cwa__chip${skills.includes(s) ? " is-on" : ""}`}
                    aria-pressed={skills.includes(s)}
                    onClick={() => toggleSkill(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="cwa__grid">
                <div className="cwa__field">
                  <label htmlFor="ap-name">Name</label>
                  <input id="ap-name" name="name" type="text" autoComplete="name"
                         value={form.name} onChange={change}
                         className={errors.name ? "is-bad" : ""} />
                  <span className="cwa__err">{errors.name || ""}</span>
                </div>
                <div className="cwa__field">
                  <label htmlFor="ap-email">Email</label>
                  <input id="ap-email" name="email" type="email" autoComplete="email"
                         value={form.email} onChange={change}
                         className={errors.email ? "is-bad" : ""} />
                  <span className="cwa__err">{errors.email || ""}</span>
                </div>
              </div>

              <div className="cwa__grid">
                <div className="cwa__field">
                  <label htmlFor="ap-phone">
                    Phone <span className="cwa__hint">(optional)</span>
                  </label>
                  <input id="ap-phone" name="phoneNumber" type="tel" autoComplete="tel"
                         value={form.phoneNumber} onChange={change} />
                  <span className="cwa__err"></span>
                </div>
                <div className="cwa__field">
                  <label htmlFor="ap-rate">Hourly rate (USD)</label>
                  <input id="ap-rate" name="rate" type="text" inputMode="numeric"
                         placeholder="e.g. 60" value={form.rate} onChange={change}
                         className={errors.rate ? "is-bad" : ""} />
                  <span className="cwa__err">{errors.rate || ""}</span>
                </div>
              </div>

              <div className="cwa__field">
                <label htmlFor="ap-links">
                  GitHub, LinkedIn or portfolio{" "}
                  <span className="cwa__hint">(optional — a link tells us more than a CV)</span>
                </label>
                <input id="ap-links" name="links" type="text"
                       placeholder="github.com/yourname · linkedin.com/in/yourname"
                       value={form.links} onChange={change} />
                <span className="cwa__err"></span>
              </div>

              <p className="cwa__legend">
                CV <span className="cwa__hint">(optional, max 25MB)</span>
              </p>
              <div className="cwa__file">
                <button type="button" className="cwa__fileBtn" onClick={pickFile}>
                  Choose file
                </button>
                <span className={`cwa__fileName${file.name ? " is-set" : ""}`}>
                  {file.name || "No file selected"}
                </span>
                <input ref={fileRef} type="file" onChange={onFile}
                       style={{ display: "none" }}
                       accept=".pdf,.doc,.docx,.txt,.rtf,.odt" />
              </div>

              <button className="cwa__submit" type="submit" disabled={sending}>
                {sending ? "Sending…" : "Send application"}
              </button>
              <p className={`cwa__note${note.bad ? " is-bad" : ""}`} role="status" aria-live="polite">
                {note.text}
              </p>
              <p className="cwa__after">
                An engineer reads every application — tell us what you've shipped.
              </p>
            </form>
          </>
        )}
      </div>

      <footer className="cwa__foot">
        © 2026 Cowork Development · <a href="mailto:contact@cowork.dev">contact@cowork.dev</a>
      </footer>
    </div>
  );
};

export default ApplyForJob;
